import {
  layoutModeChanged,
  selectCurrentRecordingMode,
} from 'features/layout/features/config/configSlice';
import { contentWidthChanged } from 'features/layout/features/contentDimensions/contentDimensionsSlice';
import { LayoutMode } from 'features/layout/types';
import { call, cancel, fork, put, select } from 'redux-saga/effects';
import { Task } from 'redux-saga';
import { startRecorder } from 'features/recorder/utils';
import { extensionWatchdogSaga } from 'features/recorder/sagas/extensionWatchdogSaga';
import { SignalingSocket } from 'services/signaling';
import { selectRoomToken } from 'features/room/roomSlice';
import { RoomToken } from 'features/room/types';
import * as Sentry from '@sentry/react';
import { layoutConfig } from 'utils/layout';
import { logger } from 'utils/logger';

// A recorder never reaches `status: 'started'` in its own store, so the phase it
// died in has to travel on the Sentry scope instead.
export const START_PHASE_TAG = 'recorder_start_phase';

export function* startRecorderSaga() {
  try {
    const token: RoomToken = yield select(selectRoomToken);
    if (!token.parsedData) {
      throw new Error('Missing token data');
    }

    const { recordingId, chunkIntervalMinutes } = token.parsedData;

    yield fork(Sentry.setTag, START_PHASE_TAG, 'extension_pending');
    logger.remote({ system: true }).info('Recorder extension asked to start', {
      recordingId,
      chunkIntervalMinutes,
    });

    const askedAt = Date.now();
    const watchdog: Task = yield fork(extensionWatchdogSaga, 'start', recordingId);

    let filename: string;
    try {
      filename = yield call(startRecorder, {
        recordingId: recordingId!,
        chunkIntervalMinutes,
      });
    } finally {
      yield cancel(watchdog);
    }

    logger.remote({ system: true }).info('Recorder extension answered the start request', {
      recordingId,
      filename,
      latencyMs: Date.now() - askedAt,
    });

    const targetMode: LayoutMode = yield select(selectCurrentRecordingMode);

    yield put(layoutModeChanged(targetMode));

    yield put(contentWidthChanged(layoutConfig.maxContentWidth));

    yield call(SignalingSocket.send, { event: 'recordingStarted' });

    yield fork(Sentry.setTag, START_PHASE_TAG, 'recording');
  } catch (error) {
    yield fork(Sentry.setTag, START_PHASE_TAG, 'failed');
    yield call(SignalingSocket.send, { event: 'recordingFailure' });

    Sentry.captureException(error, { tags: { feature: 'recorder' } });
    logger.remote({ tier: 1 }).error('Recording start failed', error);
  }
}
